import { ROUTE_PATHS } from "@bcc/lib/constants";
import { cn } from "@bcc/lib/utils";
import type { CarrierCustomerAccountBinding } from "@shared/types";
import { Link, useLocation } from "react-router-dom";
import { CarrierIcon } from "./carrier-icon";
import { OrdersCountBadge } from "../orders-count-badge";
import { setCarrierNavItemTestId } from "./carrier-nav-items.utils";

interface CarrierNavItemsProps {
  readonly carrierDetails: CarrierCustomerAccountBinding[];
  /** Map of customerAccountNumber → waiting order count */
  readonly waitingCounts?: Record<string, number>;
  readonly testId?: string;
}

/**
 * Renders carrier sub-navigation items under the Orders sidebar entry.
 * Only renders when there are 2+ carriers.
 */
export function CarrierNavItems({ carrierDetails, waitingCounts, testId }: CarrierNavItemsProps) {
  const location = useLocation();

  if (carrierDetails.length <= 1) return null;

  return (
    <div data-testid={testId} className="ml-6 mt-0.5 space-y-0.5">
      {carrierDetails.map(({ id, customerAccountDisplayName, customerAccountName, customerAccountNumber, carrier }) => {
        const path = `${ROUTE_PATHS.MARKETPLACE}/${id ?? customerAccountNumber}/orders`;
        const isActive = location.pathname === path;
        const displayName = customerAccountDisplayName || customerAccountName;
        const count = waitingCounts?.[customerAccountNumber];

        return (
          <Link
            key={customerAccountNumber}
            to={path}
            data-testid={setCarrierNavItemTestId(customerAccountNumber)}
            className={cn(
              "flex items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors",
              isActive ? "bg-accent/10 text-accent-strong font-medium" : "text-muted-foreground hover:text-foreground hover:bg-accent/5"
            )}
          >
            <CarrierIcon carrier={carrier} className="shrink-0" />
            <span className="truncate">{displayName}</span>
            <OrdersCountBadge count={count} />
          </Link>
        );
      })}
    </div>
  );
}
